import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  TableSortLabel,
  Button,
} from '@mui/material';
import { useCompany } from './CompanyContext';
import FilterToolbar from './FilterToolbar';
import { formatDate } from './utils/dateUtils';
import { debounce } from 'lodash';

const API_URL = '/api/ordenes_compra';

const statusColors = {
  'Borrador': 'default',
  'Firmado Supervisión': 'warning',
  'Firmado Dirección General': 'warning',
  'Aprobada': 'success',
  'Cancelado': '#000000',
  'Terminado': '#FFC107',
};

const OC_STATUSES = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Firmado Supervisión', label: 'Firmado Supervisión' },
  { value: 'Firmado Dirección General', label: 'Firmado Dirección General' },
  { value: 'Aprobada', label: 'Aprobada' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'terminada', label: 'Terminada' },
];

const getDefaultFilters = () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return {
    q: '',
    startDate: thirtyDaysAgo.toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    statuses: ['Borrador', 'Firmado Supervisión', 'Firmado Dirección General', 'Aprobada'], // Default active
    fechaLimitePagoFrom: '',
    fechaLimitePagoTo: '',
  };
};

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function OrdenCompraList() {
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompanyIds } = useCompany();

  // Handle URL query parameters (e.g., from dashboard traffic light)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fechaLimitePagoFrom = searchParams.get('fecha_limite_pago_from');
    const fechaLimitePagoTo = searchParams.get('fecha_limite_pago_to');

    if (fechaLimitePagoFrom) {
      setFilters(prev => ({
        ...prev,
        fechaLimitePagoFrom,
        fechaLimitePagoTo: fechaLimitePagoTo || '',
      }));
    }
  }, [location.search]);

  useEffect(() => {
    const fetchOrdenesCompra = debounce(async (currentFilters, companyIds, currentSortConfig) => {
      try {
        setLoading(true);
        const params = {
          empresa_ids: companyIds,
          q: currentFilters.q,
          start_date: currentFilters.startDate,
          end_date: (() => {
            if (!currentFilters.endDate) return '';
            const date = new Date(currentFilters.endDate);
            date.setDate(date.getDate() + 1);
            return date.toISOString().split('T')[0];
          })(),
          status: currentFilters.statuses,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };

        // Only add fecha_limite_pago params if they have values
        if (currentFilters.fechaLimitePagoFrom) {
          params.fecha_limite_pago_from = currentFilters.fechaLimitePagoFrom;
        }
        if (currentFilters.fechaLimitePagoTo) {
          params.fecha_limite_pago_to = currentFilters.fechaLimitePagoTo;
        }
        const response = await axios.get(API_URL, {
          params,
          paramsSerializer: params => {
            return Object.entries(params)
              .flatMap(([key, value]) =>
                Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`) : `${key}=${encodeURIComponent(value)}`
              )
              .join('&');
          }
        });
        setOrdenesCompra(response.data);
        setError('');
      } catch (error) {
        console.error('There was an error fetching the purchase orders!', error);
        setError('No se pudieron cargar las órdenes de compra.');
      } finally {
        setLoading(false);
      }
    }, 500);

    if (location.state?.refresh) {
      fetchOrdenesCompra.cancel();
      fetchOrdenesCompra(filters, selectedCompanyIds, sortConfig);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchOrdenesCompra(filters, selectedCompanyIds, sortConfig);
    }

    return () => {
      fetchOrdenesCompra.cancel();
    };
  }, [filters, selectedCompanyIds, sortConfig, location.state, navigate, location.pathname]);

  const handleRowClick = (id) => {
    navigate(`/ordenes-compra/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleExcelExport = async () => {
    try {
      const params = {
        empresa_ids: selectedCompanyIds,
        q: filters.q,
        start_date: filters.startDate,
        end_date: (() => {
          if (!filters.endDate) return '';
          const date = new Date(filters.endDate);
          date.setDate(date.getDate() + 1);
          return date.toISOString().split('T')[0];
        })(),
        status: filters.statuses,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      // Only add fecha_limite_pago params if they have values
      if (filters.fechaLimitePagoFrom) {
        params.fecha_limite_pago_from = filters.fechaLimitePagoFrom;
      }
      if (filters.fechaLimitePagoTo) {
        params.fecha_limite_pago_to = filters.fechaLimitePagoTo;
      }

      const response = await axios.get(`${API_URL}/export` , {
        params,
        responseType: 'blob',
        paramsSerializer: params => {
          return Object.entries(params)
            .flatMap(([key, value]) =>
              Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`) : `${key}=${encodeURIComponent(value)}`
            )
            .join('&');
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'ordenes_de_compra.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('No se pudo exportar a Excel.');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Órdenes de Compra
        </Typography>
        <Button variant="contained" color="primary" onClick={handleExcelExport}>
          Exportar a Excel
        </Button>
      </Box>

      <FilterToolbar
        filters={filters}
        onFilterChange={setFilters}
        statusOptions={OC_STATUSES}
      />

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="ordenes de compra table">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'doc_numb'}
                    direction={sortConfig.key === 'doc_numb' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('doc_numb')}
                  >
                    N° Orden
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'total'}
                    direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'proveedor'}
                    direction={sortConfig.key === 'proveedor' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('proveedor')}
                  >
                    Proveedor
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'status'}
                    direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Estado
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'created_at'}
                    direction={sortConfig.key === 'created_at' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Fecha
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'fecha_limite_pago'}
                    direction={sortConfig.key === 'fecha_limite_pago' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('fecha_limite_pago')}
                  >
                    Fecha Límite de Pago
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Última Nota</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordenesCompra.map((oc) => {
                const displayStatus = oc.status_ciclo && oc.status_ciclo !== 'activo'
                  ? oc.status_ciclo.charAt(0).toUpperCase() + oc.status_ciclo.slice(1)
                  : oc.status;
                const chipColor = statusColors[displayStatus];
                const isCustomColor = chipColor && chipColor.startsWith('#');

                return (
                  <TableRow
                    key={oc.id}
                    hover
                    onClick={() => handleRowClick(oc.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {oc.doc_numb || 'Pendiente'}
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: oc.moneda || 'MXN' }).format(oc.total)}
                    </TableCell>
                    <TableCell>{highlightText(oc.proveedor ? oc.proveedor.nombre : 'N/A', filters.q)}</TableCell>
                    <TableCell>{highlightText(oc.empresa ? oc.empresa.nombre : 'N/A', filters.q)}</TableCell>
                    <TableCell>
                      <Chip
                        label={displayStatus}
                        style={{ backgroundColor: isCustomColor ? chipColor : undefined, color: isCustomColor ? 'white' : undefined }}
                        color={!isCustomColor ? (chipColor || 'default') : undefined}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(oc.created_at)}</TableCell>
                    <TableCell>{formatDate(oc.fecha_limite_pago)}</TableCell>
                    <TableCell>
                      <Tooltip title={oc.nota_preview || ''} arrow>
                        <span>{highlightText(oc.nota_preview || 'N/A', filters.q)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default OrdenCompraList;
